import dayjs from '@/plugins/dayjs'
import type { ListParams, PaginatedResult } from '@/types/common'
import type { AuditEntry, AuditEntryInput, AuditTargetType } from '@/types/audit'
import { AUDIT_CATEGORIES } from '@/auth/auditLabels'
import { auditFixture } from '@/fixtures/audit.fixture'
import { queryList } from '@/services/mock/mockDb'
import { simulateLatency } from '@/services/mock/latency'
import { nextId } from '@/services/mock/idGenerator'
import { useAuthStore } from '@/stores/auth.store'
import { httpClient } from '@/services/http/httpClient'
import { pickService, USE_MOCKS } from '@/services/http/apiMode'

export interface AuditService {
  list(params: ListParams): Promise<PaginatedResult<AuditEntry>>
  /** Everything that happened to one record — shown on the customer profile timeline. */
  forTarget(targetType: AuditTargetType, targetId: string): Promise<AuditEntry[]>
  /** Distinct people who appear as actors, for the "Cine" filter. */
  actors(): Promise<{ id: string; name: string }[]>
  /**
   * Appends an entry. In the mock the actor comes from the signed-in user; the
   * real backend derives it server-side from the token and ignores client claims.
   */
  record(input: AuditEntryInput): Promise<AuditEntry>
}

let entries = USE_MOCKS ? [...auditFixture] : []

const mockAuditService: AuditService = {
  async list(params) {
    await simulateLatency(200, 450)
    return queryList(entries as unknown as Record<string, unknown>[], params, {
      searchableFields: ['actorName', 'targetName', 'details'],
      sortableFields: ['date', 'actorName'],
      filterMatchers: {
        category: (item, value) => {
          const category = AUDIT_CATEGORIES.find((c) => c.key === value)
          return category ? category.actions.includes((item as unknown as AuditEntry).action) : true
        },
        actorId: (item, value) => (item as unknown as AuditEntry).actorId === value,
        from: (item, value) => !dayjs((item as unknown as AuditEntry).date).isBefore(dayjs(value as string).startOf('day')),
        to: (item, value) => !dayjs((item as unknown as AuditEntry).date).isAfter(dayjs(value as string).endOf('day')),
      },
    }) as unknown as PaginatedResult<AuditEntry>
  },

  async forTarget(targetType, targetId) {
    await simulateLatency(100, 250)
    return entries.filter((e) => e.targetType === targetType && e.targetId === targetId)
  },

  async actors() {
    await simulateLatency(50, 150)
    const seen = new Map<string, string>()
    for (const entry of entries) {
      if (!entry.actorIsCustomer && !seen.has(entry.actorId)) seen.set(entry.actorId, entry.actorName)
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ro'))
  },

  async record(input) {
    const user = useAuthStore().user
    const actor = input.actor ?? (user ? { id: user.id, name: `${user.firstName} ${user.lastName}` } : { id: 'system', name: 'EReach' })
    const entry: AuditEntry = {
      action: input.action,
      targetType: input.targetType,
      targetId: input.targetId,
      targetName: input.targetName,
      details: input.details,
      id: nextId('aud'),
      actorId: actor.id,
      actorName: actor.name,
      actorIsCustomer: actor.isCustomer,
      date: dayjs().toISOString(),
      ip: '86.120.14.201',
    }
    entries = [entry, ...entries]
    return entry
  },
}

const httpAuditService: AuditService = {
  list: (params) =>
    httpClient.get('/audit-logs', { ...params, sortField: params.sort?.field, sortOrder: params.sort?.order, sort: undefined }),
  async forTarget(targetType, targetId) {
    const { items } = await httpClient.get<{ items: AuditEntry[] }>('/audit-logs', {
      pageSize: 50,
      filters: { targetType, targetId },
    })
    return items
  },
  actors: () => httpClient.get('/audit-logs/actors'),
  async record(input) {
    // The API writes its own journal entries from the actions themselves — a
    // client-supplied entry would be unverifiable, so this is a no-op there.
    return {
      ...input,
      id: 'local',
      actorId: '',
      actorName: '',
      date: new Date().toISOString(),
    } as AuditEntry
  },
}

export const auditService: AuditService = pickService(mockAuditService, httpAuditService)
